import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import Topbar from "@/components/layout/Topbar";
import { toast } from "sonner";
import {
    Database, Play, RefreshCw, CheckCircle2, XCircle, Clock,
    Download, Mail, Trash2, ShieldCheck, HardDrive, Settings as SettingsIcon,
    ChevronRight, X,
} from "lucide-react";
import { formatDateTime, relativeTime } from "@/lib/format";

const KB = 1024;
const MB = KB * KB;

function fmtBytes(n) {
    if (!n && n !== 0) return "—";
    if (n < KB) return `${n} B`;
    if (n < MB) return `${(n / KB).toFixed(1)} KB`;
    return `${(n / MB).toFixed(2)} MB`;
}

export default function SuperBackups() {
    const [tab, setTab] = useState("history"); // history | settings
    const [history, setHistory] = useState([]);
    const [kpis, setKpis] = useState(null);
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(false);
    const [running, setRunning] = useState(false);
    const [selected, setSelected] = useState(null);
    const [savingSettings, setSavingSettings] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [h, k, s] = await Promise.all([
                api.get("/superadmin/backups/history?limit=50"),
                api.get("/superadmin/backups/kpis"),
                api.get("/superadmin/backups/settings"),
            ]);
            setHistory(h.data);
            setKpis(k.data);
            setSettings(s.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const runNow = async () => {
        if (!window.confirm("Të nisim një backup manual tani?\nMund të zgjasë deri në një minutë sipas madhësisë së bazës së të dhënave.")) return;
        setRunning(true);
        try {
            const { data } = await api.post("/superadmin/backups/run-now");
            if (data.status === "success") {
                toast.success(`Backup me sukses · ${fmtBytes(data.encrypted_size_bytes)}${data.email_delivered ? ` · emaili u dërgua` : ""}`);
            } else {
                toast.error(`Backup dështoi: ${data.error || "e panjohur"}`);
            }
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setRunning(false);
        }
    };

    const resendEmail = async (id) => {
        try {
            const { data } = await api.post(`/superadmin/backups/${id}/resend-email`);
            toast.success(`Emaili ${data.delivered ? "u dërgua" : "u vendos në radhë"} · me bashkëngjitje: ${data.attached ? "po" : "jo"}`);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const download = (id) => {
        const url = `${api.defaults.baseURL}/superadmin/backups/${id}/download`;
        const token = localStorage.getItem("akm_token");
        // Fetch with auth then trigger download
        fetch(url, { headers: { Authorization: `Bearer ${token}` } })
            .then(async (r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                const blob = await r.blob();
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = r.headers.get("Content-Disposition")?.match(/filename="(.+)"/)?.[1] || "backup.enc";
                a.click();
            })
            .catch((e) => toast.error(`Shkarkimi dështoi: ${e.message}`));
    };

    const removeBackup = async (id) => {
        if (!window.confirm("Fshi këtë backup + skedarin e enkriptuar?")) return;
        try {
            await api.delete(`/superadmin/backups/${id}`);
            toast.success("U fshi");
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    const runRetention = async () => {
        if (!window.confirm("Zbato politikën e mbajtjes tani?\nRegjistrat e vjetër do të fshihen përgjithmonë.")) return;
        try {
            const { data } = await api.post("/superadmin/backups/retention/run");
            toast.success(`Politika u zbatua · ${data.deleted} regjistër u fshinë`);
            await load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        }
    };

    return (
        <>
            <Topbar
                title="Backup i Automatizuar"
                subtitle="Kopje të enkriptuara të MongoDB · dy herë në ditë · dërguar me email"
            />
            <div className="flex-1 overflow-auto p-6" data-testid="super-backups">
                <KpiStrip kpis={kpis} settings={settings} />

                <div className="mt-5 flex items-center justify-between">
                    <div className="flex gap-1" data-testid="backups-tabs">
                        <Tab active={tab === "history"} onClick={() => setTab("history")} testid="backups-tab-history">
                            Historia
                        </Tab>
                        <Tab active={tab === "settings"} onClick={() => setTab("settings")} testid="backups-tab-settings">
                            Cilësimet
                        </Tab>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={load} disabled={loading}
                            className="akm-btn-ghost h-8 text-[12px] disabled:opacity-50"
                            data-testid="backups-refresh">
                            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Rifresko
                        </button>
                        <button onClick={runRetention}
                            className="akm-btn-ghost h-8 text-[12px]"
                            data-testid="backups-run-retention">
                            <Trash2 size={12} /> Zbato mbajtjen
                        </button>
                        <button onClick={runNow} disabled={running}
                            className="rounded-lg bg-[#ff7b02] px-3 py-1.5 text-[12px] font-bold text-white shadow-sm hover:bg-[#e56d00] disabled:opacity-50"
                            data-testid="backups-run-now">
                            <Play size={12} className="inline -mt-0.5" /> {running ? "Duke u ekzekutuar…" : "Nis backup tani"}
                        </button>
                    </div>
                </div>

                {tab === "history" && (
                    <div className="mt-3 akm-card overflow-hidden" data-testid="backups-history">
                        <table className="akm-table w-full">
                            <thead>
                                <tr>
                                    <th className="text-left">Statusi</th>
                                    <th className="text-left">Lloji</th>
                                    <th className="text-left">Filloi</th>
                                    <th className="text-left">Kohëzgjatja</th>
                                    <th className="text-left">Koleksionet</th>
                                    <th className="text-left">Madhësia e enkriptuar</th>
                                    <th className="text-left">Emaili</th>
                                    <th className="text-right w-[240px]">Veprimet</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.length === 0 && !loading && (
                                    <tr>
                                        <td colSpan="8" className="py-16 text-center text-muted-foreground">
                                            <Database size={40} className="mx-auto mb-2 opacity-40" />
                                            <div className="text-[13.5px] font-semibold text-slate-700">Ende s&apos;ka backup</div>
                                            <div className="text-[11.5px]">Kliko &quot;Nis backup tani&quot; për të krijuar të parin.</div>
                                        </td>
                                    </tr>
                                )}
                                {history.map((row) => (
                                    <tr key={row.id} data-testid={`backup-row-${row.id}`}>
                                        <td>
                                            {row.status === "success" ? (
                                                <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-emerald-700">
                                                    <CheckCircle2 size={11} /> Sukses
                                                </span>
                                            ) : row.status === "running" ? (
                                                <span className="inline-flex items-center gap-1 rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-sky-700">
                                                    <Clock size={11} /> Duke u ekzekutuar
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 rounded-md bg-red-50 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider text-red-700">
                                                    <XCircle size={11} /> Dështoi
                                                </span>
                                            )}
                                        </td>
                                        <td>
                                            <span className={`inline-block rounded-md px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide ${
                                                row.kind === "monthly"
                                                    ? "bg-purple-50 text-purple-700"
                                                    : "bg-slate-100 text-slate-600"
                                            }`}>
                                                {row.kind}
                                            </span>
                                            {row.manual && (
                                                <span className="ml-1 inline-block rounded-md bg-amber-50 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">manual</span>
                                            )}
                                        </td>
                                        <td>
                                            <div className="text-[12px] text-slate-800">{formatDateTime(row.started_at)}</div>
                                            <div className="text-[10.5px] text-muted-foreground">{relativeTime(row.started_at)}</div>
                                        </td>
                                        <td className="text-[12px] font-mono text-slate-700">
                                            {row.completed_at && row.started_at
                                                ? `${Math.round((new Date(row.completed_at) - new Date(row.started_at)) / 1000)}s`
                                                : "—"}
                                        </td>
                                        <td className="text-[12px] font-mono">{row.collections_count || "—"}</td>
                                        <td className="text-[12px] font-mono">{fmtBytes(row.encrypted_size_bytes)}</td>
                                        <td className="text-[12px]">
                                            {row.email_delivered ? (
                                                <span className="text-emerald-700">
                                                    <Mail size={11} className="inline -mt-0.5" /> Dërguar
                                                </span>
                                            ) : row.email_sent_to ? (
                                                <span className="text-amber-700">Në radhë</span>
                                            ) : (
                                                <span className="text-muted-foreground">—</span>
                                            )}
                                            {row.attached_to_email && (
                                                <div className="text-[10px] text-muted-foreground">bashkëngjitje</div>
                                            )}
                                        </td>
                                        <td className="text-right">
                                            <div className="inline-flex flex-wrap justify-end gap-1">
                                                {row.status === "success" && row.local_path && (
                                                    <>
                                                        <button
                                                            onClick={() => download(row.id)}
                                                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-[#ff7b02] hover:text-[#ff7b02]"
                                                            data-testid={`backup-download-${row.id}`}
                                                            title="Shkarko arkivin e enkriptuar"
                                                        >
                                                            <Download size={11} className="inline -mt-0.5" /> .enc
                                                        </button>
                                                        <button
                                                            onClick={() => resendEmail(row.id)}
                                                            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:border-[#ff7b02] hover:text-[#ff7b02]"
                                                            data-testid={`backup-resend-${row.id}`}
                                                            title="Ridërgo emailin"
                                                        >
                                                            <Mail size={11} className="inline -mt-0.5" />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => setSelected(row)}
                                                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600 hover:bg-slate-50"
                                                    data-testid={`backup-detail-${row.id}`}
                                                >
                                                    <ChevronRight size={11} className="inline" />
                                                </button>
                                                <button
                                                    onClick={() => removeBackup(row.id)}
                                                    className="rounded-md border border-red-200 bg-white px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50"
                                                    data-testid={`backup-delete-${row.id}`}
                                                >
                                                    <Trash2 size={11} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {tab === "settings" && settings && (
                    <SettingsForm
                        settings={settings}
                        saving={savingSettings}
                        onSave={async (patch) => {
                            setSavingSettings(true);
                            try {
                                const { data } = await api.put("/superadmin/backups/settings", patch);
                                setSettings(data);
                                toast.success("Cilësimet u ruajtën");
                            } catch (e) {
                                toast.error(formatApiErrorDetail(e.response?.data?.detail));
                            } finally {
                                setSavingSettings(false);
                            }
                        }}
                    />
                )}
            </div>

            {selected && (
                <DetailDrawer row={selected} onClose={() => setSelected(null)} />
            )}
        </>
    );
}

function KpiStrip({ kpis, settings }) {
    return (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="backups-kpis">
            <Kpi label="Backup i fundit i suksesshëm" tone="emerald" icon={CheckCircle2}
                value={kpis?.last_success ? relativeTime(kpis.last_success.completed_at) : "Asnjëherë"}
                sub={kpis?.last_success ? fmtBytes(kpis.last_success.encrypted_size_bytes) : "—"} />
            <Kpi label="Total backup" tone="slate" icon={Database}
                value={kpis?.total ?? "—"}
                sub={kpis ? `${kpis.success_count} sukses · ${kpis.failed_count} dështuar` : "—"} />
            <Kpi label="Norma e suksesit" tone="orange" icon={ShieldCheck}
                value={kpis ? `${kpis.success_rate_pct}%` : "—"}
                sub={kpis?.last_failure ? `Dështimi i fundit ${relativeTime(kpis.last_failure.completed_at)}` : "Pa dështime"} />
            <Kpi label="Enkriptim + dërgim" tone="sky" icon={HardDrive}
                value={settings?.passphrase_status === "configured" ? "I sigurt" : "Çelës dev"}
                sub={`${settings?.storage_provider || "—"} · ${settings?.alert_email || "pa email"}`} />
        </div>
    );
}

const TONE_BG = { slate: "text-slate-800", emerald: "text-emerald-700",
                   orange: "text-orange-700", sky: "text-sky-700", red: "text-red-700" };
const TONE_ICON = { slate: "text-slate-400", emerald: "text-emerald-500",
                     orange: "text-orange-500", sky: "text-sky-500", red: "text-red-500" };

function Kpi({ label, value, sub, tone, icon: Icon }) {
    return (
        <div className="akm-card p-4">
            <div className="flex items-center justify-between">
                <div className="akm-section-title">{label}</div>
                {Icon && <Icon size={14} className={TONE_ICON[tone]} />}
            </div>
            <div className={`mt-1 text-xl font-semibold tracking-tight ${TONE_BG[tone]}`}>{value}</div>
            {sub && <div className="mt-0.5 text-[11px] text-muted-foreground truncate">{sub}</div>}
        </div>
    );
}

function Tab({ active, onClick, children, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`rounded-md border px-3 py-1.5 text-[12px] font-semibold transition ${
                active
                    ? "border-[#ff7b02] bg-[#ff7b02] text-white"
                    : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
            }`}
        >{children}</button>
    );
}

function SettingsForm({ settings, saving, onSave }) {
    const [state, setState] = useState({
        enabled: settings.enabled,
        alert_email: settings.alert_email || "",
        attach_to_email: settings.attach_to_email,
        max_attach_mb: settings.max_attach_mb,
        retention_daily_days: settings.retention_daily_days,
        retention_monthly_months: settings.retention_monthly_months,
    });
    const setF = (k, v) => setState((s) => ({ ...s, [k]: v }));
    return (
        <div className="mt-3 akm-card p-6 max-w-2xl" data-testid="backups-settings-form">
            <div className="mb-5 flex items-center gap-2">
                <SettingsIcon size={16} className="text-[#ff7b02]" />
                <div className="text-[14px] font-semibold text-slate-900">Konfigurimi i backup-it</div>
            </div>

            <div className="space-y-4">
                <Toggle label="Backup automatik i aktivizuar"
                    hint="Kur është i çaktivizuar, kroni dyherësh anashkalohet. Ekzekutimet manuale funksionojnë."
                    checked={state.enabled} onChange={(v) => setF("enabled", v)}
                    testid="backups-toggle-enabled" />

                <Field label="Email për njoftime"
                    hint="Ku dërgohen njoftimet e suksesit dhe dështimit. Ky është edhe marrësi i parazgjedhur i bashkëngjitjes.">
                    <input
                        type="email" value={state.alert_email}
                        onChange={(e) => setF("alert_email", e.target.value)}
                        className="akm-input h-9 w-full"
                        data-testid="backups-email-input"
                    />
                </Field>

                <Toggle label="Bashkëngjit arkivin e enkriptuar në email"
                    hint="Nëse ndalon poshtë kufirit të madhësisë; backup më të mëdhenj dërgojnë vetëm njoftim me shtegun në disk."
                    checked={state.attach_to_email} onChange={(v) => setF("attach_to_email", v)}
                    testid="backups-toggle-attach" />

                <Field label="Madhësia max e bashkëngjitjes (MB)" hint="Backup më të mëdhenj se kjo ruhen vetëm lokalisht.">
                    <input
                        type="number" min={1} max={100}
                        value={state.max_attach_mb}
                        onChange={(e) => setF("max_attach_mb", Number(e.target.value))}
                        className="akm-input h-9 w-32"
                        data-testid="backups-max-mb"
                    />
                </Field>

                <div className="grid grid-cols-2 gap-4">
                    <Field label="Mbajtja · ditore (ditë)">
                        <input
                            type="number" min={1} max={365}
                            value={state.retention_daily_days}
                            onChange={(e) => setF("retention_daily_days", Number(e.target.value))}
                            className="akm-input h-9 w-full"
                            data-testid="backups-retention-daily"
                        />
                    </Field>
                    <Field label="Mbajtja · mujore (muaj)">
                        <input
                            type="number" min={1} max={60}
                            value={state.retention_monthly_months}
                            onChange={(e) => setF("retention_monthly_months", Number(e.target.value))}
                            className="akm-input h-9 w-full"
                            data-testid="backups-retention-monthly"
                        />
                    </Field>
                </div>

                <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-[11.5px] text-slate-700">
                    <div className="mb-1 font-semibold text-slate-900">Enkriptimi</div>
                    Statusi: <b>{settings.passphrase_status === "configured" ? "I konfiguruar përmes BACKUP_ENCRYPTION_PASSPHRASE" : "Duke përdorur çelësin dev (ndryshoje para prodhimit!)"}</b><br/>
                    Ruajtja: <b>{settings.storage_provider}</b> — S3/R2 është opsional; rrjedha me email funksionon që tani.
                </div>

                <div className="pt-3">
                    <button
                        onClick={() => onSave(state)}
                        disabled={saving}
                        className="rounded-lg bg-[#ff7b02] px-4 py-2 text-[12px] font-bold text-white shadow-sm hover:bg-[#e56d00] disabled:opacity-50"
                        data-testid="backups-save-settings"
                    >
                        {saving ? "Duke ruajtur…" : "Ruaj cilësimet"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, hint, children }) {
    return (
        <div>
            <label className="mb-1 block text-[11.5px] font-semibold text-slate-800">{label}</label>
            {children}
            {hint && <div className="mt-1 text-[10.5px] text-muted-foreground">{hint}</div>}
        </div>
    );
}

function Toggle({ label, hint, checked, onChange, testid }) {
    return (
        <div className="flex items-start justify-between gap-4">
            <div>
                <div className="text-[12.5px] font-semibold text-slate-800">{label}</div>
                {hint && <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>}
            </div>
            <button
                onClick={() => onChange(!checked)}
                data-testid={testid}
                className={`relative h-5 w-9 shrink-0 rounded-full transition ${checked ? "bg-[#ff7b02]" : "bg-slate-300"}`}
            >
                <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${checked ? "left-4" : "left-0.5"}`} />
            </button>
        </div>
    );
}

function DetailDrawer({ row, onClose }) {
    return (
        <div className="fixed inset-0 z-50" data-testid="backup-detail-drawer">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="absolute right-0 top-0 h-full w-[560px] max-w-full overflow-y-auto bg-white shadow-2xl">
                <div className="sticky top-0 flex items-center justify-between border-b border-slate-200 bg-white px-6 py-4">
                    <div>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Detaje Backup</div>
                        <div className="text-[13px] font-mono text-slate-900">{row.id}</div>
                    </div>
                    <button onClick={onClose} className="akm-btn-ghost h-8 w-8" data-testid="backup-drawer-close">
                        <X size={14} />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <Pair k="Statusi" v={row.status} />
                    <Pair k="Lloji" v={row.kind} />
                    <Pair k="Manual" v={row.manual ? "po" : "jo"} />
                    <Pair k="Filloi" v={formatDateTime(row.started_at)} />
                    <Pair k="Përfundoi" v={formatDateTime(row.completed_at)} />
                    <Pair k="Metoda e dump-it" v={row.dump_method || "—"} />
                    <Pair k="Koleksionet" v={row.collections_count} />
                    <Pair k="Madhësia raw" v={fmtBytes(row.raw_size_bytes)} />
                    <Pair k="Madhësia e enkriptuar" v={fmtBytes(row.encrypted_size_bytes)} />
                    <Pair k="Marrësi i emailit" v={row.email_sent_to || "—"} />
                    <Pair k="Emaili u dërgua" v={row.email_delivered ? "po" : "jo"} />
                    <Pair k="Bashkëngjitur në email" v={row.attached_to_email ? "po" : "jo"} />
                    <Pair k="Shtegu lokal" v={<span className="font-mono text-[11px]">{row.local_path || "—"}</span>} />
                    <Pair k="Aktori" v={row.actor || "system"} />
                    {row.error && (
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Gabim</div>
                            <pre className="mt-1 whitespace-pre-wrap rounded-md border border-red-200 bg-red-50 p-3 text-[11.5px] text-red-800">
                                {row.error}
                            </pre>
                        </div>
                    )}
                    {row.collection_stats && (
                        <div>
                            <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Dokumente për koleksion</div>
                            <div className="max-h-64 overflow-auto rounded-md border border-slate-200 bg-slate-50 p-3 text-[11px] font-mono">
                                {Object.entries(row.collection_stats).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
                                    <div key={k} className="flex justify-between">
                                        <span>{k}</span><span>{v}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function Pair({ k, v }) {
    return (
        <div>
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground">{k}</div>
            <div className="mt-0.5 text-[13px] text-slate-800">{v}</div>
        </div>
    );
}
